import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  HandHeart, 
  Users, 
  BookOpen, 
  ChatCircleDots, 
  CalendarCheck,
  List,
  X,
  User,
  SignOut,
  ArrowRight,
  Gear
} from '@phosphor-icons/react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthModal from './components/AuthModal';
import ChatBot from './components/ChatBot';
import ContactSection from './components/ContactSection';
import ResourceLibrary from './components/ResourceLibrary';
import ForumSection from './components/ForumSection';
import TestimonialsSection from './components/TestimonialsSection';
import AdminDashboard from './components/AdminDashboard';
import InstallPrompt from './components/InstallPrompt';
import ShareButton from './components/ShareButton';
import './App.css';

// Navigation Component
const Navigation = ({ onAuthClick, onDashboardClick }) => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: '#about', label: 'About' },
    { href: '#features', label: 'How It Works' },
    { href: '#testimonials', label: 'Testimonials' },
    { href: '#resources', label: 'Resources' },
    { href: '#community', label: 'Community' },
    { href: '#contact', label: 'Contact' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-[#F9F6F0]/80 backdrop-blur-xl border-b border-[#E5DCD0]" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3" data-testid="logo">
            <div className="w-10 h-10 bg-[#B46C4A] rounded-full flex items-center justify-center">
              <Heart size={20} weight="fill" className="text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-xl text-[#2A2825]">Safe Space</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-[#5C564F] hover:text-[#B46C4A] transition-colors font-medium"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-4">
                {user.role === 'admin' && (
                  <button
                    onClick={onDashboardClick}
                    className="flex items-center gap-2 text-[#B46C4A] hover:text-[#9B5C3E] transition-colors font-medium"
                    data-testid="dashboard-btn"
                  >
                    <Gear size={20} weight="fill" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </button>
                )}
                <span className="hidden sm:flex items-center gap-2 text-[#5C564F]">
                  <User size={18} />
                  {user.name}
                </span>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 text-[#5C564F] hover:text-[#B46C4A] transition-colors"
                  data-testid="logout-btn"
                >
                  <SignOut size={20} />
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => onAuthClick('login')}
                className="bg-[#B46C4A] hover:bg-[#9B5C3E] text-white px-6 py-2.5 rounded-full font-medium transition-all duration-300"
                data-testid="login-nav-btn"
              >
                Sign In
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-[#2A2825]"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-white border-t border-[#E5DCD0]"
        >
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-[#5C564F] hover:text-[#B46C4A] transition-colors font-medium py-2"
              >
                {link.label}
              </a>
            ))}
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onAuthClick }) => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/188005d7-e4ad-4124-adeb-36b0fb46f144/images/dd3ba52691d868e111d2ae0ac406285fa7740be2935f69326d4984bc02010b5f.png"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9F6F0] via-[#F9F6F0]/90 to-[#F9F6F0]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            {/* Peer Support Badge */}
            <div className="inline-flex items-center gap-2 bg-[#F2EBE1] text-[#5C564F] px-4 py-2 rounded-full text-sm mb-6">
              <HandHeart size={18} weight="fill" className="text-[#B46C4A]" />
              Peer Support for the Black Community
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Outfit'] font-medium text-[#2A2825] tracking-tight leading-tight mb-6">
              You Don't Have to<br />
              <span className="text-[#B46C4A]">Carry It Alone</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#5C564F] leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              Safe Space by Adeseun is here to listen, support, and walk alongside you. Whether you need someone to talk to or just a moment of peace — you're welcome here.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={() => onAuthClick('register')}
                className="bg-[#B46C4A] hover:bg-[#9B5C3E] text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-cta-btn"
              >
                Start Your Journey <ArrowRight size={20} weight="bold" />
              </button>
              <a
                href="#about"
                className="bg-[#F2EBE1] hover:bg-[#E5DCD0] text-[#2A2825] px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 text-center"
                data-testid="hero-learn-more"
              >
                Learn More
              </a>
            </div>

            {/* Disclaimer */}
            <p className="mt-8 text-sm text-[#5C564F]/70 max-w-md mx-auto lg:mx-0">
              <span className="font-medium">Note:</span> This is a peer support service, not licensed therapy. For emergencies, please contact a crisis hotline.
            </p>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block"
          >
            <img
              src="https://static.prod-images.emergentagent.com/jobs/188005d7-e4ad-4124-adeb-36b0fb46f144/images/685dee9f8a17ff72e3fc49dedef3655ad1a14e8c475ccaebef9b8e74ae2c54d6.png"
              alt="People supporting each other"
              className="w-full rounded-3xl shadow-2xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 sm:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="https://static.prod-images.emergentagent.com/jobs/188005d7-e4ad-4124-adeb-36b0fb46f144/images/73f68bd15171c32cf3d2307c1bb908a20852b848461e8558c73b4b5c6dac1124.png"
              alt="Peaceful safe space"
              className="w-full rounded-3xl shadow-lg"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium text-[#2A2825] mb-6">
              About Safe Space
            </h2>

            <div className="space-y-6 text-[#5C564F] leading-relaxed">
              <p>
                Hi, I'm <span className="font-semibold text-[#2A2825]">Adeseun</span>. I created Safe Space because I understand how it feels to need someone who truly gets it — someone who looks like you, who understands your experiences, and who won't judge.
              </p>
              <p>
                As a member of the Black community, I know that mental health support that feels culturally safe and understanding can be hard to find. That's why I'm here — to offer a listening ear, a moment of connection, and the reminder that you matter.
              </p>
              <p>
                This isn't therapy. It's peer support from someone who cares. Whether you're dealing with stress, anxiety, life transitions, or just need someone to talk to — you're welcome here.
              </p>
            </div>

            <div className="mt-8 p-6 bg-[#F2EBE1] rounded-2xl">
              <p className="text-[#5C564F] italic">
                "Sometimes the bravest thing you can do is ask for help. I'm honored you're considering letting me be part of your journey."
              </p>
              <p className="mt-3 font-medium text-[#2A2825]">— Adeseun</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: ChatCircleDots,
      title: "AI Support Companion",
      description: "Chat with our empathetic AI anytime you need a listening ear. Available 24/7 for immediate support."
    },
    {
      icon: CalendarCheck,
      title: "Book a Session",
      description: "Schedule a one-on-one conversation with Ade for more personalized peer support."
    },
    {
      icon: Users,
      title: "Community Forum",
      description: "Connect with others who understand. Share experiences, find support, and know you're not alone."
    },
    {
      icon: BookOpen,
      title: "Resource Library",
      description: "Access curated mental health resources, crisis hotlines, and self-help tools."
    },
    {
      icon: HandHeart,
      title: "Anonymous Messaging",
      description: "Share what's on your mind without revealing your identity. Complete privacy, complete safety."
    },
    {
      icon: Heart,
      title: "Culturally Safe",
      description: "A space created specifically for the Black community, with understanding and cultural awareness."
    }
  ];

  return (
    <section id="features" className="py-24 sm:py-32 bg-[#F9F6F0]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium text-[#2A2825] mb-4">
            How Safe Space Supports You
          </h2>
          <p className="text-lg text-[#5C564F] max-w-2xl mx-auto">
            Multiple ways to find the support you need, whenever you need it.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-3xl p-8 border border-[#E5DCD0] hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-14 h-14 bg-[#B46C4A]/10 rounded-2xl flex items-center justify-center mb-6">
                <feature.icon size={28} weight="fill" className="text-[#B46C4A]" />
              </div>
              <h3 className="text-xl font-['Outfit'] font-semibold text-[#2A2825] mb-3">
                {feature.title}
              </h3>
              <p className="text-[#5C564F] leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#2A2825] text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#B46C4A] rounded-full flex items-center justify-center">
                <Heart size={20} weight="fill" className="text-white" />
              </div>
              <span className="font-['Outfit'] font-semibold text-xl">Safe Space by Adeseun</span>
            </div>
            <p className="text-white/70 leading-relaxed max-w-md">
              A peer support platform created for the Black community. You're not alone, and help is always available.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Outfit'] font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-white/70">
              <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
              <li><a href="#resources" className="hover:text-white transition-colors">Resources</a></li>
              <li><a href="#community" className="hover:text-white transition-colors">Community</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Outfit'] font-semibold mb-4">Contact Adeseun</h4>
            <ul className="space-y-2 text-white/70">
              <li>
                <a href="mailto:Adeseun.a@yahoo.com" className="hover:text-white transition-colors">
                  Adeseun.a@yahoo.com
                </a>
              </li>
              <li>
                <a href="tel:07480927718" className="hover:text-white transition-colors">
                  07480927718
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center text-white/50 text-sm">
          <p className="mb-2">© 2024 Safe Space by Adeseun. All rights reserved.</p>
          <p>This is a peer support service, not licensed therapy. In emergencies, please contact a crisis hotline.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App Content
const AppContent = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showDashboard, setShowDashboard] = useState(false);

  const handleAuthClick = (mode = 'login') => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  if (showDashboard) {
    return <AdminDashboard onClose={() => setShowDashboard(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Navigation onAuthClick={handleAuthClick} onDashboardClick={() => setShowDashboard(true)} />
      
      <main>
        <HeroSection onAuthClick={handleAuthClick} />
        <AboutSection />
        <FeaturesSection />
        <TestimonialsSection />
        <ResourceLibrary />
        <ForumSection onAuthRequired={() => handleAuthClick('login')} />
        <ContactSection />
      </main>

      <Footer />

      {/* Chat Bot */}
      <ChatBot />

      {/* Share Button */}
      <ShareButton />

      {/* Install Prompt */}
      <InstallPrompt />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        initialMode={authMode}
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
